/**
 * my-page「看板」タブの案件検索（案件キー / 案件名の部分一致）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 担当案件の検索語の正規化と、統合プロダクト配下チャンクの絞り込み
 * @graph-connects none
 */
import type {
  MyProjectsBoardIntegratedProduct,
  PmoOverdueProject,
} from '../../../types/project.js';

/** 大文字小文字・全角半角を無視するための正規化（NFKC + 小文字化 + 前後空白除去）。 @graph-connects none */
export function normalizeSearchText(text: string): string {
  return text.normalize('NFKC').toLowerCase().trim();
}

/** 案件キーまたは案件名が検索語を含むか。検索語が空なら常に true。 @graph-connects none */
export function matchesProjectQuery(query: string, issueKey: string, summary: string): boolean {
  const q = normalizeSearchText(query);
  if (q === '') return true;
  return normalizeSearchText(issueKey).includes(q) || normalizeSearchText(summary).includes(q);
}

/** 絞り込んだ統合プロダクト。matchedChunks が true なら配下チャンクの一致で残った（自動展開の対象）。 */
export interface FilteredIntegratedProduct<T extends MyProjectsBoardIntegratedProduct> {
  /** 絞り込み後の統合プロダクト */
  ip: T;
  /** 配下チャンクの一致で残ったか */
  matchedChunks: boolean;
}

/**
 * 統合プロダクトを検索語で絞り込む。
 * 統合プロダクト自身が一致すれば配下を全部残し、そうでなければ一致したチャンクだけ残して展開対象にする。
 * @graph-connects none
 */
export function filterIntegratedProducts<T extends MyProjectsBoardIntegratedProduct>(
  query: string,
  products: T[],
): FilteredIntegratedProduct<T>[] {
  if (normalizeSearchText(query) === '') {
    return products.map((ip) => ({ ip, matchedChunks: false }));
  }
  return products.flatMap((ip): FilteredIntegratedProduct<T>[] => {
    if (matchesProjectQuery(query, ip.integratedProductKey, ip.summary)) {
      return [{ ip, matchedChunks: false }];
    }
    const chunkProjects = ip.chunkProjects.filter((p) =>
      matchesProjectQuery(query, p.issueKey, p.summary),
    );
    if (chunkProjects.length === 0) return [];
    return [{ ip: { ...ip, chunkProjects } as T, matchedChunks: true }];
  });
}

/** 案件一覧を検索語で絞り込む。 @graph-connects none */
export function filterProjects(query: string, projects: PmoOverdueProject[]): PmoOverdueProject[] {
  return projects.filter((p) => matchesProjectQuery(query, p.issueKey, p.summary));
}
